// クリップの個別/複数エクスポート・インポート用フォーマット（.fmclippack.json）。
// .fmavatar とは異なり、meshIndex ではなくシェイプキー名をキーにして
// 別アバターへの移植を可能にする。

import type { BlendshapeValue, ClipKeyframe, FaceClip, TrackingSetting } from "./types.ts";
import { fail } from "./errors.ts";

export interface ClipPackShapeValue {
  name: string;
  value: number;
}

export interface ClipPackKeyframe {
  time: number;
  shapes: ClipPackShapeValue[];
}

export interface ClipPackClip {
  name: string;
  type: "static" | "keyframe";
  durationSeconds: number;
  shapes: ClipPackShapeValue[];
  keyframes?: ClipPackKeyframe[];
  loop?: boolean;
  tracking?: TrackingSetting;
}

export interface ClipPack {
  formatVersion: 1;
  clips: ClipPackClip[];
}

function stripMeshIndex(shapes: BlendshapeValue[]): ClipPackShapeValue[] {
  return shapes.map((s) => ({ name: s.name, value: s.value }));
}

export function buildClipPack(clips: FaceClip[]): ClipPack {
  return {
    formatVersion: 1,
    clips: clips.map((c) => {
      const packed: ClipPackClip = {
        name: c.name,
        type: c.type,
        durationSeconds: c.durationSeconds,
        shapes: stripMeshIndex(c.shapes),
      };
      if (c.keyframes) {
        packed.keyframes = c.keyframes.map((k) => ({ time: k.time, shapes: stripMeshIndex(k.shapes) }));
      }
      if (c.loop !== undefined) packed.loop = c.loop;
      if (c.tracking !== undefined) packed.tracking = c.tracking;
      return packed;
    }),
  };
}

export interface ApplyClipPackResult {
  clips: Array<Omit<FaceClip, "index">>;
  missingShapeNames: string[];
}

export function applyClipPack(
  pack: ClipPack,
  avatarMeshes: { index: number; blendshapeNames: string[] }[],
): ApplyClipPackResult {
  // 同名シェイプが複数メッシュにある場合に備え、名前 → 該当する全 meshIndex を持つ
  const nameToMeshIndices = new Map<string, number[]>();
  for (const mesh of avatarMeshes) {
    for (const name of new Set(mesh.blendshapeNames)) {
      const list = nameToMeshIndices.get(name);
      if (list) list.push(mesh.index);
      else nameToMeshIndices.set(name, [mesh.index]);
    }
  }

  const missing = new Set<string>();
  const resolveShapes = (shapes: ClipPackShapeValue[]): BlendshapeValue[] => {
    // パック内で同名が複数回出る（元アバターで複数メッシュにあった）場合は、出現順に
    // 該当メッシュへ割り当てる。出現数がメッシュ数より少なければ残りのメッシュには
    // 最後の値を適用する。多すぎる分は捨てる。いずれにせよ (meshIndex, name) の重複は作らない。
    const valuesByName = new Map<string, number[]>();
    for (const s of shapes) {
      const list = valuesByName.get(s.name);
      if (list) list.push(s.value);
      else valuesByName.set(s.name, [s.value]);
    }
    const result: BlendshapeValue[] = [];
    for (const [name, values] of valuesByName) {
      const meshIndices = nameToMeshIndices.get(name);
      if (!meshIndices) {
        missing.add(name);
        continue;
      }
      meshIndices.forEach((meshIndex, i) => {
        result.push({ meshIndex, name, value: values[Math.min(i, values.length - 1)] });
      });
    }
    return result;
  };

  const clips: Array<Omit<FaceClip, "index">> = pack.clips.map((c) => {
    const clip: Omit<FaceClip, "index"> = {
      name: c.name,
      type: c.type,
      durationSeconds: c.durationSeconds,
      assetGuid: null,
      shapes: resolveShapes(c.shapes),
    };
    if (c.keyframes) {
      clip.keyframes = c.keyframes.map((k): ClipKeyframe => ({ time: k.time, shapes: resolveShapes(k.shapes) }));
    }
    if (c.loop !== undefined) clip.loop = c.loop;
    if (c.tracking !== undefined) clip.tracking = c.tracking;
    return clip;
  });

  return { clips, missingShapeNames: [...missing] };
}

const WHERE = "クリップパック";
const HINT = "FaceMaker で書き出した .fmclippack.json を選んでください。";

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function checkShapes(v: unknown, path: string): ClipPackShapeValue[] {
  if (!Array.isArray(v)) fail(WHERE, `${path} が配列ではありません。`, HINT);
  return v.map((s, i) => {
    const o = s as Record<string, unknown> | null;
    if (typeof o !== "object" || o === null) fail(WHERE, `${path}[${i}] がオブジェクトではありません。`, HINT);
    if (typeof o.name !== "string" || o.name === "") fail(WHERE, `${path}[${i}].name が不正です。`, HINT);
    if (!isFiniteNumber(o.value) || o.value < -1000 || o.value > 1000) {
      fail(WHERE, `${path}[${i}].value は -1000〜1000 の数値である必要があります。`, HINT);
    }
    return { name: o.name, value: o.value };
  });
}

/**
 * JSON 文字列を ClipPack として解析・検証する。構造が不正なら FmError を投げる
 * （一部だけ取り込むことはしない）。
 */
export function parseClipPack(json: string): ClipPack {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    fail(WHERE, "JSON として読み取れませんでした。", HINT);
  }
  const root = raw as Record<string, unknown> | null;
  if (typeof root !== "object" || root === null || Array.isArray(root)) fail(WHERE, "ルートがオブジェクトではありません。", HINT);
  if (root.formatVersion !== 1) fail(WHERE, `未対応の formatVersion です: ${String(root.formatVersion)}`, HINT);
  if (!Array.isArray(root.clips)) fail(WHERE, "clips が配列ではありません。", HINT);

  const clips: ClipPackClip[] = root.clips.map((c, ci) => {
    const p = `clips[${ci}]`;
    const o = c as Record<string, unknown> | null;
    if (typeof o !== "object" || o === null) fail(WHERE, `${p} がオブジェクトではありません。`, HINT);
    if (typeof o.name !== "string") fail(WHERE, `${p}.name が文字列ではありません。`, HINT);
    if (o.type !== "static" && o.type !== "keyframe") fail(WHERE, `${p}.type は "static" か "keyframe" である必要があります。`, HINT);
    if (!isFiniteNumber(o.durationSeconds) || o.durationSeconds < 0) fail(WHERE, `${p}.durationSeconds が不正です。`, HINT);
    const clip: ClipPackClip = {
      name: o.name,
      type: o.type,
      durationSeconds: o.durationSeconds,
      shapes: checkShapes(o.shapes, `${p}.shapes`),
    };
    if (o.keyframes !== undefined) {
      if (!Array.isArray(o.keyframes)) fail(WHERE, `${p}.keyframes が配列ではありません。`, HINT);
      clip.keyframes = o.keyframes.map((k, ki) => {
        const kp = `${p}.keyframes[${ki}]`;
        const ko = k as Record<string, unknown> | null;
        if (typeof ko !== "object" || ko === null) fail(WHERE, `${kp} がオブジェクトではありません。`, HINT);
        if (!isFiniteNumber(ko.time) || ko.time < 0) fail(WHERE, `${kp}.time は 0 以上の数値である必要があります。`, HINT);
        return { time: ko.time, shapes: checkShapes(ko.shapes, `${kp}.shapes`) };
      });
    }
    if (clip.type === "keyframe" && (!clip.keyframes || clip.keyframes.length < 2)) {
      fail(WHERE, `${p} は keyframe クリップですがキーフレームが 2 枚未満です。`, HINT);
    }
    if (o.loop !== undefined) {
      if (typeof o.loop !== "boolean") fail(WHERE, `${p}.loop が真偽値ではありません。`, HINT);
      clip.loop = o.loop;
    }
    if (o.tracking !== undefined) {
      const t = o.tracking as Record<string, unknown> | null;
      if (
        typeof t !== "object" || t === null ||
        (t.eyes !== "animation" && t.eyes !== "tracking") ||
        (t.mouth !== "expression" && t.mouth !== "lipsync")
      ) {
        fail(WHERE, `${p}.tracking が不正です。`, HINT);
      }
      clip.tracking = { eyes: t.eyes, mouth: t.mouth } as TrackingSetting;
    }
    return clip;
  });
  return { formatVersion: 1, clips };
}

/** ダウンロード用のファイル名を作る（禁止文字を除去）。 */
export function clipPackFileName(baseName: string): string {
  const safe = baseName.replace(/[\\/:*?"<>|]/g, "_").trim() || "clips";
  return `${safe}.fmclippack.json`;
}
